import { z } from 'zod';

/** Longest single message a manager may send. Generous for "why not Haaland,
 * I don't trust Saka's fitness, and what does a -8 cost me?" and nowhere near
 * long enough to be a way of stuffing the context window. */
const MESSAGE_MAX_LENGTH = 2000;

/**
 * One turn of an argument about an analysis. Only the two conversational
 * roles are modelled: the system prompt and the tool-use plumbing live in
 * `services/chat.ts` and are never round-tripped through the client, so a
 * caller cannot rewrite either.
 */
export const ChatMessage = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string().min(1).max(MESSAGE_MAX_LENGTH),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

/**
 * The chat itself is stateless — the client holds the conversation and sends
 * it back each turn, the same way the Messages API works underneath. History
 * is capped server-side (see `MAX_HISTORY_MESSAGES`), so a long argument
 * cannot grow the request without limit.
 */
export const ChatRequest = z.object({
  /** The squad under discussion. `POST /api/squads/:id/chat` takes this from
   * its path param instead, so the body carries only history and message. */
  squadId: z.string().min(1),
  history: z.array(ChatMessage).default([]),
  message: z.string().min(1).max(MESSAGE_MAX_LENGTH),
});
export type ChatRequest = z.infer<typeof ChatRequest>;

/** The deterministic functions the model is allowed to consult. Each name
 * maps to an existing scoring/planning function — the model never does the
 * arithmetic itself. */
export const ChatToolName = z.enum(['score_player', 'plan_transfers', 'check_transfer']);
export type ChatToolName = z.infer<typeof ChatToolName>;

/**
 * A reply plus the tools it was built from, in call order and with repeats
 * kept — "asked the planner twice" is worth showing. `toolsUsed` is how the
 * UI can say which numbers are real rather than asking the manager to take
 * the prose on trust.
 */
export const ChatResponse = z.object({
  reply: z.string(),
  toolsUsed: z.array(ChatToolName).default([]),
});
export type ChatResponse = z.infer<typeof ChatResponse>;
